import { Title } from '@solidjs/meta'
import { useParams, useSearchParams } from '@solidjs/router'
import { type Component, createEffect, createMemo, createResource, createSignal, onCleanup, Show } from 'solid-js'
import Avatar from '~/components/Avatar'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import Card from '~/components/Card'
import FileInput from '~/components/FileInput'
import Form from '~/components/Form'
import GeoSelects from '~/components/GeoSelects'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import MapView from '~/components/Map'
import Modal from '~/components/Modal'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import Tabs from '~/components/Tabs'
import Textarea from '~/components/Textarea'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompanySettings } from '~/hooks/useCompanySettings'
import { usePermissions } from '~/hooks/usePermissions'
import {
  documentColor,
  documentIcon,
  formatBytes,
  MAX_DOCUMENT_BYTES,
  TabActividades,
  TabBancos,
  TabComercial,
  TabContactos,
  TabDirecciones,
  TabDocumentos,
  TabGeneral,
  TabNotas,
  TabResumen,
  TabTributario
} from '~/layers/ui/admin/clientes'
import { type Path, useNavigate } from '~/router'
import { type CustomerAddressType, type CustomerBankType, type CustomerContactType, type CustomerDocumentType, type CustomerType, customerService } from '~/services/customer.service'
import { parameterService } from '~/services/parameter.service'
import { setTrailOverride } from '~/stores/breadcrumb'
import type { TabItemType } from '~/types/Tabs'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'
import { addressLabelOptions, bankAccountTypeOptions, classificationOptions, currencyOptions, customerStateColors, customerStateLabels, documentTypeOptions } from '~/utils/settings'

/** Categorías de documento: parámetros de este tipo, gestionados en Parámetros. */
const DOCUMENT_CATEGORY_TYPE = 'categoria_documento'

/** Igual que el límite del backend (services/storage.ts): 10 MB. */
/** El FormData devuelve FormDataEntryValue (string | File): para inputs es siempre string. */
const str = (v: unknown) => (typeof v === 'string' ? v : undefined)
const strOrUndef = (v: unknown) => {
  const s = str(v)
  return s?.trim() ? s : undefined
}
/** El Switch envía un input oculto con 'true'/'false'; un checkbox nativo enviaría 'on'. */
const isOn = (v: unknown) => v === 'on' || v === 'true'

/** 'CO' era el valor por defecto histórico del campo pais: se muestra y se guarda como 'Colombia'. */
const geoPais = (v: unknown) => {
  const s = typeof v === 'string' && v.trim() ? v : ''
  return s === 'CO' ? 'Colombia' : s
}

interface CustomerTabsPropsType {
  items: TabItemType[]
  initialTab?: string
  onTabChange?: (tab: string) => void
}

const CUSTOMER_TAB_IDS = ['resumen', 'general', 'tributario', 'comercial', 'contactos', 'direcciones', 'bancos', 'documentos', 'actividades', 'notas']

/**
 * Las pestañas viven en su propio componente para que cambiar de tab no
 * re-renderice toda la página (antes ~200ms por clic): el estado activeTab
 * está aislado aquí.
 */
const CustomerTabs: Component<CustomerTabsPropsType> = props => {
  const sanitizeTab = (tab: string | undefined) => (tab && CUSTOMER_TAB_IDS.includes(tab) ? tab : 'resumen')
  const [activeTab, setActiveTab] = createSignal(sanitizeTab(props.initialTab))
  return (
    <Tabs
      items={props.items}
      activeTab={activeTab()}
      onChange={tab => {
        setActiveTab(tab)
        props.onTabChange?.(tab)
      }}
      variant="underline"
      transition="fade"
      showContent
      keepMounted
    />
  )
}

const CustomerDetailPage: Component = () => {
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const toast = useToast()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const alert = useAlert()
  const { settings } = useCompanySettings()

  /** Cambiar de pestaña se refleja en la URL (?tab=...) para poder recargar y compartir la vista. */
  const handleTabChange = (tab: string) => {
    setSearchParams({ ...searchParams, tab }, { replace: true })
  }

  /** Catálogos y formatos vienen de `companies.settings` (configurables). */
  const estadoColor = () => customerStateColors(settings()?.customerStates ?? [])
  const estadoLabel = () => customerStateLabels(settings()?.customerStates ?? [])
  const documentOptions = () => documentTypeOptions(settings()?.documentTypes ?? [])
  const monedaOptions = () => currencyOptions(settings()?.currencies ?? [])
  const clasificacionOptions = () => classificationOptions(settings()?.customerClassifications ?? [])
  const bancoOptions = () => bankAccountTypeOptions(settings()?.bankAccountTypes ?? [])
  const direccionOptions = () => addressLabelOptions(settings()?.addressLabels ?? [])
  const origenOptions = () => [
    { value: 'web', title: 'Web' },
    { value: 'referido', title: 'Referido' },
    { value: 'catalogo', title: 'Catálogo' },
    { value: 'otro', title: 'Otro' }
  ]
  const estadoClienteOptions = () =>
    settings()
      ?.customerStates.slice()
      .sort((a, b) => a.order - b.order)
      .map(s => ({ value: s.key, title: s.label })) ?? []

  const customerId = () => Number(params.id)

  const [customer, { refetch }] = createResource(async () => {
    try {
      return await customerService.get(customerId())
    } catch {
      return null
    }
  })
  const [isSaving, setIsSaving] = createSignal(false)

  /** Categorías de documento: se crean en Parámetros, como las del catálogo. */
  const [documentCategories] = createResource(async () => {
    try {
      const result = await parameterService.list({ type: DOCUMENT_CATEGORY_TYPE, limit: 200 })
      return result.items
    } catch {
      return []
    }
  })
  const documentCategoryOptions = () => [{ value: '', title: 'Sin categoría' }, ...(documentCategories() ?? []).map(p => ({ value: String(p.id), title: p.label }))]

  const [contactModal, setContactModal] = createSignal(false)
  const [editingContact, setEditingContact] = createSignal<CustomerContactType | null>(null)
  const [addressModal, setAddressModal] = createSignal(false)
  const [editingAddress, setEditingAddress] = createSignal<CustomerAddressType | null>(null)
  const [editingBank, setEditingBank] = createSignal<CustomerBankType | null>(null)
  /** Geometría del mapa del modal de dirección: punto (lat/lng) o polígono. */
  const [addressLat, setAddressLat] = createSignal<number | null>(null)
  const [addressLng, setAddressLng] = createSignal<number | null>(null)
  const [addressPolygon, setAddressPolygon] = createSignal<Array<[number, number]>>([])
  const [addressDrawing, setAddressDrawing] = createSignal(false)
  /** Centro inicial del mapa: el punto guardado o Colombia por defecto. Solo cambia al abrir el modal. */
  const [addressMapCenter, setAddressMapCenter] = createSignal<[number, number]>([4.711, -74.072])
  /** Aplica los resultados de geocodificación inversa a los campos del formulario de dirección. */
  const applyGeocodeToForm = (result: { address?: string; postalCode?: string; city?: string; state?: string; neighborhood?: string }) => {
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set
    const setField = (name: string, value: string) => {
      const el = document.querySelector(`#address-form [name="${name}"]`) as HTMLInputElement | null
      if (el && value) {
        setter?.call(el, value)
        el.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }
    setField('address', result.address || '')
    setField('barrio', result.neighborhood || '')
    setField('codigoPostal', result.postalCode || '')
    setAddressFormValues({
      address: result.address,
      barrio: result.neighborhood,
      codigoPostal: result.postalCode,
      ciudad: result.city,
      departamento: result.state
    })
  }

  /** Campos del formulario de dirección que se actualizan desde el mapa (geocodificación inversa). */
  const [addressFormValues, setAddressFormValues] = createSignal<{
    address?: string
    barrio?: string
    codigoPostal?: string
    ciudad?: string
    departamento?: string
  }>({})
  const [bankModal, setBankModal] = createSignal(false)
  const [noteModal, setNoteModal] = createSignal(false)
  const [noteValue, setNoteValue] = createSignal('')
  const [documentModal, setDocumentModal] = createSignal(false)
  /** Con documento => el modal edita metadatos; sin él => sube uno nuevo. */
  const [editingDocument, setEditingDocument] = createSignal<CustomerDocumentType | null>(null)
  const [uploading, setUploading] = createSignal(false)
  const [activityModal, setActivityModal] = createSignal(false)
  const [activityTitle, setActivityTitle] = createSignal('')
  const [activityDesc, setActivityDesc] = createSignal('')

  const c = () => customer()

  const fullName = () => {
    const data = c()
    if (!data) return ''
    if (data.tipoPersona === 'juridica') return data.razonSocial || data.name
    return [data.name, data.lastName].filter(Boolean).join(' ')
  }

  // Miga de pan propia: "CRM › Clientes › [nombre]". Se limpia al salir de la ficha.
  createEffect(() => {
    const name = fullName()
    if (name) setTrailOverride(['CRM', 'Clientes', name])
  })
  onCleanup(() => setTrailOverride(null))

  // El título se precalcula en un memo (creado en el mount, dentro del root):
  // un ternario dentro del children del <Title> compila a un _$memo que
  // @solidjs/meta crea perezosamente al limpiar el tag, fuera de un root →
  // "computations created outside a `createRoot`".
  const pageTitle = createMemo(() => (fullName() ? `${fullName()} | Florez Jaramillo` : 'Cliente | Florez Jaramillo'))

  const handleSaveGeneral = async (formData: FormData) => {
    if (!c()) return
    setIsSaving(true)
    try {
      const payload = {
        tipoPersona: (str(formData.get('tipoPersona')) as 'natural' | 'juridica') ?? 'natural',
        documentType: str(formData.get('documentType')) || 'CC',
        document: strOrUndef(formData.get('document')),
        digitoVerificacion: strOrUndef(formData.get('digitoVerificacion')),
        name: str(formData.get('name')) || c()!.name,
        lastName: strOrUndef(formData.get('lastName')),
        razonSocial: strOrUndef(formData.get('razonSocial')),
        nombreComercial: strOrUndef(formData.get('nombreComercial')),
        phone: strOrUndef(formData.get('phone')),
        celular: strOrUndef(formData.get('celular')),
        email: strOrUndef(formData.get('email')),
        paginaWeb: strOrUndef(formData.get('paginaWeb')),
        address: strOrUndef(formData.get('address')),
        barrio: strOrUndef(formData.get('barrio')),
        ciudad: strOrUndef(formData.get('ciudad')),
        departamento: strOrUndef(formData.get('departamento')),
        pais: str(formData.get('pais')) || 'CO',
        codigoPostal: strOrUndef(formData.get('codigoPostal'))
      }
      await customerService.update(customerId(), payload)
      toast.addToast({ title: 'Cliente actualizado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveTributario = async (formData: FormData) => {
    if (!c()) return
    setIsSaving(true)
    try {
      const payload = {
        regimenTributario: strOrUndef(formData.get('regimenTributario')),
        responsableIva: isOn(formData.get('responsableIva')),
        autoretenedor: isOn(formData.get('autoretenedor')),
        granContribuyente: isOn(formData.get('granContribuyente')),
        agenteRetencion: isOn(formData.get('agenteRetencion')),
        codigoCiiu: strOrUndef(formData.get('codigoCiiu')),
        obligacionesFiscales: strOrUndef(formData.get('obligacionesFiscales'))
      }
      await customerService.update(customerId(), payload)
      toast.addToast({ title: 'Información tributaria guardada', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveComercial = async (formData: FormData) => {
    if (!c()) return
    setIsSaving(true)
    try {
      const payload = {
        cupoCredito: Number(str(formData.get('cupoCredito'))) || 0,
        diasCredito: Number(str(formData.get('diasCredito'))) || 0,
        descuentoPorDefecto: Number(str(formData.get('descuentoPorDefecto'))) || 0,
        moneda: str(formData.get('moneda')) || 'COP',
        correoFacturacion: strOrUndef(formData.get('correoFacturacion')),
        aceptaCredito: isOn(formData.get('aceptaCredito')),
        origenCliente: strOrUndef(formData.get('origenCliente')),
        clasificacion: strOrUndef(formData.get('clasificacion')),
        estadoCliente: (str(formData.get('estadoCliente')) as CustomerType['estadoCliente']) ?? 'prospecto',
        calificacion: Number(str(formData.get('calificacion'))) || 0
      }
      await customerService.update(customerId(), payload)
      toast.addToast({ title: 'Información comercial guardada', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddContact = async (formData: FormData) => {
    try {
      const payload = {
        name: str(formData.get('name')) || 'Contacto',
        position: strOrUndef(formData.get('position')),
        email: strOrUndef(formData.get('email')),
        phone: strOrUndef(formData.get('phone')),
        celular: strOrUndef(formData.get('celular')),
        notes: strOrUndef(formData.get('notes')),
        principal: isOn(formData.get('principal'))
      }
      if (editingContact()) {
        await customerService.updateContact(customerId(), editingContact()!.id, payload)
        toast.addToast({ title: 'Contacto actualizado', type: 'success' })
      } else {
        await customerService.addContact(customerId(), payload)
        toast.addToast({ title: 'Contacto añadido', type: 'success' })
      }
      setContactModal(false)
      setEditingContact(null)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const openContactModal = (contact?: CustomerContactType) => {
    setEditingContact(contact ?? null)
    setContactModal(true)
  }

  const closeContactModal = () => {
    setContactModal(false)
    setEditingContact(null)
  }

  const handleAddAddress = async (formData: FormData) => {
    try {
      const payload = {
        label: strOrUndef(formData.get('label')),
        address: str(formData.get('address')) || 'Dirección',
        barrio: strOrUndef(formData.get('barrio')),
        ciudad: strOrUndef(formData.get('ciudad')),
        departamento: strOrUndef(formData.get('departamento')),
        pais: str(formData.get('pais')) || 'CO',
        codigoPostal: strOrUndef(formData.get('codigoPostal')),
        principal: isOn(formData.get('principal')),
        lat: addressLat() ?? undefined,
        lng: addressLng() ?? undefined,
        polygon: addressPolygon().length >= 3 ? addressPolygon() : undefined
      }
      if (editingAddress()) {
        await customerService.updateAddress(customerId(), editingAddress()!.id, payload)
        toast.addToast({ title: 'Dirección actualizada', type: 'success' })
      } else {
        await customerService.addAddress(customerId(), payload)
        toast.addToast({ title: 'Dirección añadida', type: 'success' })
      }
      setAddressModal(false)
      setEditingAddress(null)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const openAddressModal = (address?: CustomerAddressType) => {
    setEditingAddress(address ?? null)
    setAddressLat(address?.lat ?? null)
    setAddressLng(address?.lng ?? null)
    setAddressPolygon(address?.polygon ?? [])
    setAddressDrawing(false)
    setAddressFormValues({})
    const lat = address?.lat
    const lng = address?.lng
    setAddressMapCenter(typeof lat === 'number' && typeof lng === 'number' ? [lat, lng] : [4.711, -74.072])
    setAddressModal(true)
  }

  const closeAddressModal = () => {
    setAddressModal(false)
    setEditingAddress(null)
    setAddressFormValues({})
  }

  /** Un clic en el mapa: si se está dibujando, añade un vértice; si no, coloca el marcador. */
  const handleAddressMapClick = (latlng: { lat: number; lng: number }) => {
    if (addressDrawing()) {
      setAddressPolygon(points => [...points, [latlng.lat, latlng.lng]])
      return
    }
    setAddressPolygon([])
    setAddressLat(latlng.lat)
    setAddressLng(latlng.lng)
  }

  const handleAddBank = async (formData: FormData) => {
    try {
      await customerService.addBank(customerId(), {
        banco: str(formData.get('banco')) || 'Banco',
        tipoCuenta: strOrUndef(formData.get('tipoCuenta')),
        numeroCuenta: strOrUndef(formData.get('numeroCuenta')),
        titular: strOrUndef(formData.get('titular')),
        documento: strOrUndef(formData.get('documento')),
        principal: isOn(formData.get('principal'))
      })
      toast.addToast({ title: 'Banco añadido', type: 'success' })
      setBankModal(false)
      setEditingBank(null)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const handleUpdateBank = async (formData: FormData) => {
    const bank = editingBank()
    if (!bank) return
    try {
      await customerService.updateBank(customerId(), bank.id, {
        banco: str(formData.get('banco')) || 'Banco',
        tipoCuenta: strOrUndef(formData.get('tipoCuenta')),
        numeroCuenta: strOrUndef(formData.get('numeroCuenta')),
        titular: strOrUndef(formData.get('titular')),
        documento: strOrUndef(formData.get('documento')),
        principal: isOn(formData.get('principal'))
      })
      toast.addToast({ title: 'Banco actualizado', type: 'success' })
      setBankModal(false)
      setEditingBank(null)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const openBankModal = (bank?: CustomerBankType) => {
    setEditingBank(bank ?? null)
    setBankModal(true)
  }

  const handleAddNote = async () => {
    if (!noteValue().trim()) return
    try {
      await customerService.addNote(customerId(), noteValue().trim())
      toast.addToast({ title: 'Nota añadida', type: 'success' })
      setNoteValue('')
      setNoteModal(false)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const handleAddActivity = async () => {
    if (!activityTitle().trim()) return
    try {
      await customerService.addActivity(customerId(), {
        type: 'custom',
        title: activityTitle().trim(),
        description: activityDesc().trim() || undefined
      })
      toast.addToast({ title: 'Actividad registrada', type: 'success' })
      setActivityTitle('')
      setActivityDesc('')
      setActivityModal(false)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  /**
   * El archivo sale del propio FormData del <Form>: `new FormData(form)` recoge
   * el <input type="file"> como File real, así que no hace falta una señal para
   * guardarlo. Ojo: el FileInput de la librería NO sirve aquí, porque serializa
   * su valor con JSON.stringify y un File se convierte en {}.
   */
  const handleSaveDocument = async (formData: FormData) => {
    const categoryId = Number(str(formData.get('categoryId')) || 0) || null
    const description = strOrUndef(formData.get('description')) ?? null

    // Editando: sólo viajan los metadatos, el archivo se queda como está.
    const editing = editingDocument()
    if (editing) {
      setUploading(true)
      try {
        await customerService.updateDocument(customerId(), editing.id, { categoryId, description })
        toast.addToast({ title: 'Documento actualizado', type: 'success' })
        closeDocumentModal()
        await refetch()
      } catch (err: unknown) {
        toast.addToast({ title: resolveError(err), type: 'danger' })
      } finally {
        setUploading(false)
      }
      return
    }

    const file = formData.get('file')
    if (!(file instanceof File) || !file.size) {
      toast.addToast({ title: 'Elige un archivo', type: 'warning' })
      return
    }
    if (file.size > MAX_DOCUMENT_BYTES) {
      toast.addToast({ title: `El archivo supera los ${MAX_DOCUMENT_BYTES / 1024 / 1024} MB`, type: 'danger' })
      return
    }
    setUploading(true)
    try {
      await customerService.uploadDocument(customerId(), file, { categoryId, description: description ?? undefined })
      toast.addToast({ title: 'Documento adjuntado', type: 'success' })
      closeDocumentModal()
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setUploading(false)
    }
  }

  const openDocumentModal = (doc?: CustomerDocumentType) => {
    setEditingDocument(doc ?? null)
    setDocumentModal(true)
  }

  const closeDocumentModal = () => {
    setDocumentModal(false)
    setEditingDocument(null)
  }

  /** El endpoint pide cabeceras de sesión, así que se descarga y se abre como blob. */
  const openDocument = async (doc: CustomerDocumentType) => {
    try {
      const url = await customerService.fetchDocumentBlobUrl(doc)
      window.open(url, '_blank')
      // Se libera tarde: si se revoca al instante, la pestaña nueva no llega a leerlo.
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const removeDocument = async (doc: CustomerDocumentType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar documento',
      message: `¿Seguro que quieres eliminar "${doc.name}"? También se borrará del servidor.`
    })
    if (!ok) return
    try {
      await customerService.deleteDocument(customerId(), doc.id)
      toast.addToast({ title: 'Documento eliminado', type: 'success' })
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const removeContact = async (id: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar contacto',
      message: '¿Estás seguro de eliminar este contacto?'
    })
    if (!ok) return
    try {
      await customerService.deleteContact(customerId(), id)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const removeAddress = async (id: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar dirección',
      message: '¿Estás seguro de eliminar esta dirección?'
    })
    if (!ok) return
    try {
      await customerService.deleteAddress(customerId(), id)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const removeBank = async (id: number) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar cuenta bancaria',
      message: '¿Estás seguro de eliminar esta cuenta bancaria?'
    })
    if (!ok) return
    try {
      await customerService.deleteBank(customerId(), id)
      await refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const initialValues = (data: CustomerType) => ({
    tipoPersona: data.tipoPersona ?? 'natural',
    documentType: data.documentType ?? 'CC',
    document: data.document ?? '',
    digitoVerificacion: data.digitoVerificacion ?? '',
    name: data.name ?? '',
    lastName: data.lastName ?? '',
    razonSocial: data.razonSocial ?? '',
    nombreComercial: data.nombreComercial ?? '',
    phone: data.phone ?? '',
    celular: data.celular ?? '',
    email: data.email ?? '',
    paginaWeb: data.paginaWeb ?? '',
    address: data.address ?? '',
    barrio: data.barrio ?? '',
    ciudad: data.ciudad ?? '',
    departamento: data.departamento ?? '',
    pais: geoPais(data.pais) || 'Colombia',
    codigoPostal: data.codigoPostal ?? '',
    regimenTributario: data.regimenTributario ?? '',
    codigoCiiu: data.codigoCiiu ?? '',
    obligacionesFiscales: data.obligacionesFiscales ?? '',
    cupoCredito: String(data.cupoCredito ?? 0),
    diasCredito: String(data.diasCredito ?? 0),
    descuentoPorDefecto: String(data.descuentoPorDefecto ?? 0),
    moneda: data.moneda ?? 'COP',
    correoFacturacion: data.correoFacturacion ?? '',
    origenCliente: data.origenCliente ?? '',
    clasificacion: data.clasificacion ?? '',
    estadoCliente: data.estadoCliente ?? 'prospecto',
    calificacion: String(data.calificacion ?? 0)
  })

  // El array de pestañas se memoiza: construirlo en cada render (9 pestañas con
  // formularios, tablas y modales) hacía que cada clic de tab tardara ~200ms.
  // Solo se reconstruye cuando cambia el cliente o los catálogos que lee.
  const tabItems = (data: CustomerType): TabItemType[] => [
    {
      id: 'resumen',
      label: 'Resumen',
      icon: 'material-symbols-light:space-dashboard-outline',
      content: <TabResumen data={data} />
    },
    {
      id: 'general',
      label: 'General',
      icon: 'material-symbols-light:person-outline',
      content: (
        <TabGeneral
          data={data}
          customerId={customerId}
          refetch={refetch}
          isSaving={isSaving}
          can={can}
          handleSaveGeneral={handleSaveGeneral}
          initialValues={initialValues}
          documentOptions={documentOptions}
        />
      )
    },
    {
      id: 'tributario',
      label: 'Tributario',
      icon: 'material-symbols-light:account-balance-outline',
      content: <TabTributario data={data} customerId={customerId} refetch={refetch} isSaving={isSaving} can={can} handleSaveTributario={handleSaveTributario} initialValues={initialValues} />
    },
    {
      id: 'comercial',
      label: 'Comercial',
      icon: 'material-symbols-light:storefront-outline',
      content: (
        <TabComercial
          data={data}
          customerId={customerId}
          refetch={refetch}
          isSaving={isSaving}
          can={can}
          handleSaveComercial={handleSaveComercial}
          initialValues={initialValues}
          monedaOptions={monedaOptions}
          clasificacionOptions={clasificacionOptions}
          origenOptions={origenOptions}
          estadoClienteOptions={estadoClienteOptions}
        />
      )
    },
    {
      id: 'contactos',
      label: `Contactos (${data.contacts?.length ?? 0})`,
      icon: 'material-symbols-light:contacts-outline',
      content: (
        <TabContactos
          data={data}
          customerId={customerId}
          refetch={refetch}
          isSaving={isSaving}
          can={can}
          handleAddContact={handleAddContact}
          editingContact={editingContact}
          openContactModal={openContactModal}
          closeContactModal={closeContactModal}
          contactModal={contactModal}
          setContactModal={setContactModal}
          setEditingContact={setEditingContact}
          removeContact={removeContact}
        />
      )
    },
    {
      id: 'direcciones',
      label: `Direcciones (${data.addresses?.length ?? 0})`,
      icon: 'material-symbols-light:location-on-outline',
      content: (
        <TabDirecciones
          data={data}
          customerId={customerId}
          refetch={refetch}
          isSaving={isSaving}
          can={can}
          handleAddAddress={handleAddAddress}
          editingAddress={editingAddress}
          openAddressModal={openAddressModal}
          closeAddressModal={closeAddressModal}
          addressModal={addressModal}
          setAddressModal={setAddressModal}
          setEditingAddress={setEditingAddress}
          removeAddress={removeAddress}
          direccionOptions={direccionOptions}
        />
      )
    },
    {
      id: 'bancos',
      label: `Bancos (${data.banks?.length ?? 0})`,
      icon: 'material-symbols-light:account-balance-outline',
      content: (
        <TabBancos
          data={data}
          customerId={customerId}
          refetch={refetch}
          isSaving={isSaving}
          can={can}
          handleAddBank={handleAddBank}
          handleUpdateBank={handleUpdateBank}
          editingBank={editingBank}
          openBankModal={openBankModal}
          setBankModal={setBankModal}
          setEditingBank={setEditingBank}
          removeBank={removeBank}
          bancoOptions={bancoOptions}
        />
      )
    },
    {
      id: 'documentos',
      label: `Documentos (${data.documents?.length ?? 0})`,
      icon: 'material-symbols-light:folder-outline',
      content: (
        <TabDocumentos
          data={data}
          customerId={customerId}
          refetch={refetch}
          isSaving={isSaving}
          can={can}
          handleSaveDocument={handleSaveDocument}
          editingDocument={editingDocument}
          openDocumentModal={openDocumentModal}
          openDocument={openDocument}
          closeDocumentModal={closeDocumentModal}
          documentModal={documentModal}
          setDocumentModal={setDocumentModal}
          setEditingDocument={setEditingDocument}
          removeDocument={removeDocument}
          documentCategoryOptions={documentCategoryOptions}
          uploading={uploading}
        />
      )
    },
    {
      id: 'actividades',
      label: 'Actividades',
      icon: 'material-symbols-light:history-outline',
      content: (
        <TabActividades
          data={data}
          customerId={customerId}
          refetch={refetch}
          isSaving={isSaving}
          can={can}
          handleAddActivity={handleAddActivity}
          activityTitle={activityTitle}
          setActivityTitle={setActivityTitle}
          activityDescription={activityDesc}
          setActivityDescription={setActivityDesc}
          activityModal={activityModal}
          setActivityModal={setActivityModal}
        />
      )
    },
    {
      id: 'notas',
      label: 'Notas',
      icon: 'material-symbols-light:sticky-note-2-outline',
      content: (
        <TabNotas
          data={data}
          customerId={customerId}
          refetch={refetch}
          isSaving={isSaving}
          can={can}
          handleAddNote={handleAddNote}
          noteValue={noteValue}
          setNoteValue={setNoteValue}
          noteModal={noteModal}
          setNoteModal={setNoteModal}
        />
      )
    }
  ]
  let cachedItemsData: CustomerType | null = null
  let cachedTabItems: TabItemType[] = []
  const getTabItems = (data: CustomerType): TabItemType[] => {
    if (cachedItemsData !== data) {
      cachedItemsData = data
      cachedTabItems = tabItems(data)
    }
    return cachedTabItems
  }

  return (
    <>
      <Title>{pageTitle()}</Title>

      <Show when={customer.error}>
        <Card>
          <Card.Body>
            <div class="text-center py-10 text-danger">No se pudo cargar el cliente: {resolveError(customer.error)}</div>
            <div class="text-center">
              <Button variant="soft" color="info" onClick={() => navigate('/admin/clientes' as Path)}>
                Volver al listado
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Show>

      <Show when={c()}>
        {data => (
          <>
            <div class="rounded-2xl border border-border bg-surface overflow-hidden mb-6">
              <div class="h-20 bg-linear-to-r from-primary-500/80 via-primary-500/40 to-transparent" />
              <div class="px-6 pb-5 -mt-9">
                <div class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div class="flex items-end gap-4">
                    <div class="rounded-2xl bg-surface p-1 border border-border shadow-sm">
                      <Avatar alt={fullName() || 'Cliente'} size={64} class="rounded-xl" />
                    </div>
                    <div class="pb-1 min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <h1 class="text-xl font-bold text-foreground truncate">{fullName() || 'Cliente'}</h1>
                        <Badge color={estadoColor()[data().estadoCliente]}>{estadoLabel()[data().estadoCliente] ?? data().estadoCliente}</Badge>
                        <Show when={data().bloqueado}>
                          <Badge color="danger">Bloqueado</Badge>
                        </Show>
                      </div>
                      <p class="text-sm text-foreground-subtle">
                        {data().documentType} {data().document || '—'}
                        {data().email ? ` · ${data().email}` : ''}
                      </p>
                    </div>
                  </div>
                  <div class="flex gap-2 pb-1">
                    <Button size="sm" variant="soft" color="default" onClick={() => navigate('/admin/clientes' as Path)} prependIcon="material-symbols-light:arrow-back">
                      Volver
                    </Button>
                    <Show when={can(P.customers.delete)}>
                      <Button
                        size="sm"
                        variant="soft"
                        color="danger"
                        onClick={async () => {
                          const ok = await alert.confirmDelete({
                            title: 'Eliminar cliente',
                            message: '¿Eliminar este cliente?'
                          })
                          if (!ok) return
                          await customerService.delete(customerId())
                          navigate('/admin/clientes' as Path)
                        }}
                        icon="material-symbols-light:delete-outline"
                      >
                        Eliminar
                      </Button>
                    </Show>
                  </div>
                </div>
              </div>
            </div>

            <CustomerTabs items={getTabItems(data())} initialTab={typeof searchParams.tab === 'string' ? searchParams.tab : undefined} onTabChange={handleTabChange} />
          </>
        )}
      </Show>

      {/* ── MODALES ─────────────────────────────────────────────────────────── */}
      <Modal isOpen={contactModal()} onClose={closeContactModal} size="sm">
        <Modal.Header>
          <h2 class="text-lg font-bold text-foreground">{editingContact() ? 'Editar Contacto' : 'Nuevo Contacto'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form
            id="contact-form"
            onSubmit={handleAddContact}
            initialValues={
              editingContact()
                ? {
                    name: editingContact()?.name ?? '',
                    position: editingContact()?.position ?? '',
                    email: editingContact()?.email ?? '',
                    phone: editingContact()?.phone ?? '',
                    celular: editingContact()?.celular ?? '',
                    notes: editingContact()?.notes ?? '',
                    principal: !!editingContact()?.principal
                  }
                : undefined
            }
          >
            <div class="space-y-4">
              <Input name="name" label="Nombre" required />
              <Input name="position" label="Cargo" />
              <Input name="email" label="Correo" type="email" />
              <Input name="phone" label="Teléfono" />
              <Input name="celular" label="Celular" />
              <Textarea name="notes" label="Notas" rows={2} />
              <Switch name="principal" label="Contacto principal" checked={!!editingContact()?.principal} />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" color="default" onClick={closeContactModal}>
              Cancelar
            </Button>
            <Button type="submit" form="contact-form">
              {editingContact() ? 'Guardar' : 'Añadir'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal isOpen={addressModal()} onClose={() => setAddressModal(false)} size="xl">
        <Modal.Header>
          <h2 class="text-lg font-bold text-foreground">{editingAddress() ? 'Editar Dirección' : 'Nueva Dirección'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form
            id="address-form"
            onSubmit={handleAddAddress}
            initialValues={
              editingAddress()
                ? {
                    label: editingAddress()?.label ?? '',
                    address: addressFormValues().address ?? editingAddress()?.address ?? '',
                    barrio: addressFormValues().barrio ?? editingAddress()?.barrio ?? '',
                    codigoPostal: addressFormValues().codigoPostal ?? editingAddress()?.codigoPostal ?? '',
                    principal: !!editingAddress()?.principal
                  }
                : {
                    address: addressFormValues().address ?? '',
                    barrio: addressFormValues().barrio ?? '',
                    codigoPostal: addressFormValues().codigoPostal ?? '',
                    principal: false
                  }
            }
          >
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div class="space-y-4 min-w-0">
                <Select name="label" label="Etiqueta" options={direccionOptions()} value={editingAddress()?.label ?? ''} />
                <Input name="address" label="Dirección" required />
                <Input name="barrio" label="Barrio" />
                <GeoSelects
                  namePais="pais"
                  nameDepartamento="departamento"
                  nameCiudad="ciudad"
                  valuePais={geoPais(editingAddress()?.pais) || 'Colombia'}
                  valueDepartamento={addressFormValues().departamento ?? editingAddress()?.departamento ?? ''}
                  valueCiudad={addressFormValues().ciudad ?? editingAddress()?.ciudad ?? ''}
                />
                <Input name="codigoPostal" label="Código postal" />
                <Switch name="principal" label="Dirección principal" checked={!!editingAddress()?.principal} />
              </div>

              {/* Ubicación en el mapa: clic coloca el marcador; en modo polígono
                  cada clic añade un vértice. El punto se guarda como lat/lng y el
                  polígono como lista de [lat, lng]. */}
              <div class="min-w-0">
                <MapView
                  center={addressMapCenter()}
                  zoom={13}
                  height="320px"
                  zoomControl
                  layerControl
                  geolocationControl
                  fullscreenControl
                  reverseGeocode
                  reverseGeocodeProvider="google"
                  onMapClick={handleAddressMapClick}
                  onGeolocate={({ lat, lng }) => {
                    setAddressPolygon([])
                    setAddressLat(lat)
                    setAddressLng(lng)
                  }}
                  onReverseGeocode={result => {
                    applyGeocodeToForm({
                      address: result.address,
                      postalCode: result.postalCode,
                      city: result.city,
                      state: result.state,
                      neighborhood: typeof result.raw?.neighborhood === 'string' ? result.raw.neighborhood : undefined
                    })
                  }}
                  markers={
                    typeof addressLat() === 'number' && typeof addressLng() === 'number' ? [{ position: [addressLat() as number, addressLng() as number], title: 'Ubicación de la dirección' }] : []
                  }
                  polygons={addressPolygon().length >= 2 ? [{ points: addressPolygon(), color: '#eab308', fillColor: '#eab308', fillOpacity: 0.15 }] : []}
                />
                <div class="flex items-center gap-2 mt-2">
                  <Show
                    when={!addressDrawing()}
                    fallback={
                      <Button
                        type="button"
                        size="sm"
                        color="warning"
                        variant="soft"
                        class="flex-1 min-w-0"
                        onClick={() => {
                          if (addressPolygon().length < 3) {
                            toast.addToast({ title: 'El polígono necesita al menos 3 puntos', type: 'warning' })
                            return
                          }
                          setAddressDrawing(false)
                        }}
                        prependIcon="material-symbols-light:check"
                      >
                        Terminar polígono ({addressPolygon().length} pts)
                      </Button>
                    }
                  >
                    <Button
                      type="button"
                      size="sm"
                      variant="soft"
                      color="warning"
                      class="flex-1 min-w-0"
                      onClick={() => {
                        setAddressPolygon([])
                        setAddressLat(null)
                        setAddressLng(null)
                        setAddressDrawing(true)
                      }}
                      prependIcon="material-symbols-light:polyline"
                    >
                      Dibujar polígono
                    </Button>
                  </Show>
                  <Show when={addressLat() !== null || addressPolygon().length > 0}>
                    <Button
                      type="button"
                      size="sm"
                      variant="soft"
                      color="default"
                      class="flex-1 min-w-0"
                      onClick={() => {
                        setAddressLat(null)
                        setAddressLng(null)
                        setAddressPolygon([])
                        setAddressDrawing(false)
                      }}
                      prependIcon="material-symbols-light:delete-sweep-outline"
                    >
                      Limpiar ubicación
                    </Button>
                  </Show>
                </div>
                <p class="text-xs text-foreground-subtle mt-2">{addressDrawing() ? 'Clic en el mapa para añadir vértices al polígono.' : 'Clic en el mapa para marcar la ubicación.'}</p>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" color="default" onClick={() => setAddressModal(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="address-form">
              {editingAddress() ? 'Guardar' : 'Añadir'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* El archivo viaja en el propio FormData del Form: sin señales de por medio. */}
      <Modal isOpen={documentModal()} onClose={closeDocumentModal} size="sm">
        <Modal.Header>
          <h2 class="text-lg font-bold text-foreground">{editingDocument() ? 'Editar Documento' : 'Subir Documento'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form
            id="document-form"
            onSubmit={handleSaveDocument}
            initialValues={editingDocument() ? { categoryId: String(editingDocument()?.categoryId ?? ''), description: editingDocument()?.description ?? '' } : undefined}
          >
            <div class="space-y-4">
              {/* Al editar no se reemplaza el archivo: se muestra cuál es y se
                    editan sólo sus datos. Para cambiarlo, se sube otro documento. */}
              <Show
                when={!editingDocument()}
                fallback={
                  <div class="flex items-center gap-3 rounded-xl border border-border bg-surface-muted/30 p-3">
                    <span class={`size-9 shrink-0 rounded-lg flex items-center justify-center ${documentColor(editingDocument()?.mimeType ?? '')}`}>
                      <Icon icon={documentIcon(editingDocument()?.mimeType ?? '')} size={20} />
                    </span>
                    <div class="min-w-0">
                      <p class="text-sm font-medium text-foreground truncate">{editingDocument()?.name}</p>
                      <p class="text-xs text-foreground-subtle">{formatBytes(editingDocument()?.size ?? 0)}</p>
                    </div>
                  </div>
                }
              >
                <FileInput
                  name="file"
                  maxFiles={1}
                  maxSize={MAX_DOCUMENT_BYTES}
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt"
                  helperText={`PDF, imagen, Word o Excel · hasta ${MAX_DOCUMENT_BYTES / 1024 / 1024} MB`}
                />
              </Show>
              <Select name="categoryId" label="Categoría" options={documentCategoryOptions()} value={String(editingDocument()?.categoryId ?? '')} />
              <p class="-mt-2 text-xs text-foreground-subtle">Las categorías se crean en Parámetros, con tipo {DOCUMENT_CATEGORY_TYPE}</p>
              <Textarea name="description" label="Descripción" rows={2} />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" color="default" onClick={closeDocumentModal}>
              Cancelar
            </Button>
            <Button type="submit" form="document-form" loading={uploading()} disabled={uploading()}>
              {editingDocument() ? 'Guardar' : 'Subir'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal
        isOpen={bankModal()}
        onClose={() => {
          setBankModal(false)
          setEditingBank(null)
        }}
        size="sm"
      >
        <Modal.Header>
          <h2 class="text-lg font-bold text-foreground">{editingBank() ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}</h2>
        </Modal.Header>
        <Modal.Body>
          <Form
            id="bank-form"
            onSubmit={editingBank() ? handleUpdateBank : handleAddBank}
            initialValues={
              editingBank()
                ? {
                    banco: editingBank()!.banco,
                    tipoCuenta: editingBank()!.tipoCuenta ?? '',
                    numeroCuenta: editingBank()!.numeroCuenta ?? '',
                    titular: editingBank()!.titular ?? '',
                    documento: editingBank()!.documento ?? '',
                    principal: editingBank()!.principal
                  }
                : undefined
            }
          >
            <div class="space-y-4">
              <Input name="banco" label="Banco" required />
              <Select name="tipoCuenta" label="Tipo de cuenta" options={bancoOptions()} value={editingBank()?.tipoCuenta ?? ''} />
              <Input name="numeroCuenta" label="Número de cuenta" />
              <Input name="titular" label="Titular" />
              <Input name="documento" label="Documento" />
              <Switch name="principal" label="Cuenta principal" checked={editingBank()?.principal ?? false} />
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button
              variant="soft"
              color="default"
              onClick={() => {
                setBankModal(false)
                setEditingBank(null)
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" form="bank-form">
              {editingBank() ? 'Guardar' : 'Añadir'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal isOpen={noteModal()} onClose={() => setNoteModal(false)} size="sm">
        <Modal.Header>
          <h2 class="text-lg font-bold text-foreground">Nueva Nota</h2>
        </Modal.Header>
        <Modal.Body>
          <Textarea label="Nota" rows={4} value={noteValue()} onInput={e => setNoteValue(e.currentTarget.value)} placeholder="Observación interna sobre el cliente..." />
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" color="default" onClick={() => setNoteModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddNote}>Guardar nota</Button>
          </div>
        </Modal.Footer>
      </Modal>

      <Modal isOpen={activityModal()} onClose={() => setActivityModal(false)} size="sm">
        <Modal.Header>
          <h2 class="text-lg font-bold text-foreground">Registrar Actividad</h2>
        </Modal.Header>
        <Modal.Body>
          <div class="space-y-4">
            <Input label="Título" value={activityTitle()} onInput={e => setActivityTitle(e.currentTarget.value)} placeholder="Llamada, reunión, seguimiento..." required />
            <Textarea label="Descripción" rows={3} value={activityDesc()} onInput={e => setActivityDesc(e.currentTarget.value)} />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div class="flex justify-end gap-2">
            <Button variant="soft" color="default" onClick={() => setActivityModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddActivity}>Registrar</Button>
          </div>
        </Modal.Footer>
      </Modal>

      <alert.AlertComponent />
    </>
  )
}

export default CustomerDetailPage
